import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Panel width override (default max-w-2xl). */
  className?: string;
  /** When false, ESC / backdrop-click / the ✕ are all suppressed — the modal can be
   *  closed only by an explicit in-content action. Use for a mid-submit lock or a
   *  one-time secret that must be acknowledged (default true). */
  dismissable?: boolean;
}

/**
 * Minimal accessible modal — no dependency. Portal to <body>, dimmed backdrop
 * (click-outside + ESC to close), body scroll-lock while open, and focus moved into
 * the dialog on open. Reusable across screens (Éligibles export/import, etc.).
 */
export function Modal({ open, onClose, title, children, className, dismissable = true }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, dismissable]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        // Close only on a click that both starts AND lands on the backdrop itself.
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn('my-8 w-full max-w-2xl rounded-2xl bg-white shadow-xl outline-none', className)}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {dismissable && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="max-h-[78vh] overflow-y-auto custom-scrollbar p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
